<template>
  <div>
    <b-row class="mb-1">
      <b-col v-if="!noOpen" class="col-auto">
        <h4 class="mb-0">
          <i class="fa fa-fw fa-images mt-1"></i>
        </h4>
      </b-col>
      <b-col>
        <b-form-input
          v-model="query"
          :placeholder="trans('search')"
          type="search"
          size="sm"
          class="mb-1"
          @search="onSearch"
          @keydown.enter="onSearch"
        />
        <tags-filter
          :organization="organization"
          multiple
          v-model="tags"
          type="image"
          @input="onSearch"
          allow-none
          :is-none="tag_is_none"
          @is-none="tag_is_none = $event"
          :all-tags="all_tags"
          @all-tags="all_tags = $event"
        ></tags-filter>
        <b-dropdown v-if="!hideUpload" variant="light" size="sm" :disabled="busy">
          <template v-slot:button-content>
            <span :class="!filterType ? 'text-muted' : (filterType == 'archived' ? 'text-danger' : 'text-primary')">
              <i class="fa fa-fw fa-filter"></i>
              {{ trans(!filterType ? 'filter' : filterType) }}
            </span>
          </template>
          <b-dropdown-item @click="filterType = null; onClickColumnFilter(null)">
            <i :class="`fa fa-fw ${!filterType ? 'fa-check' : ''}`"></i>
            {{ trans('none') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickColumnFilter('archived')">
            <i :class="`fa fa-fw ${filterType == 'archived' ? 'fa-check' : ''} text-danger`"></i>
            <span class="text-danger">{{ trans('archived') }}</span>
          </b-dropdown-item>
        </b-dropdown>
        <small class="text-muted ml-2">
          {{ totalRows }}
        </small>
      </b-col>
      <b-col class="col-auto">
        <b-button v-if="!hideUpload" size="sm" variant="primary" class="py-0" :title="trans('upload')" :disabled="busy" v-b-modal.upload-image-modal>
          <i class="fa fa-fw fa-cloud-upload-alt"></i>
          {{ trans('upload') }}
        </b-button>
        <b-dropdown v-if="canvaApi && !hideCanva" text="Canva" variant="canva" size="sm" class="ml-2" toggle-class="py-0" :disabled="busy" right>
          <b-dropdown-item @click="onClickCanva(1200, 200)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_super_thin') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCanva(1200, 400)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_thin') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCanva(1200, 600)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_wide') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCanva(1200, 900)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_landscape') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCanva(1200, 1200)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_square') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCanva(1200, 1800)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_portrait') }}
          </b-dropdown-item>
        </b-dropdown>
        <b-button size="sm" variant="link" class="pb-0" @click="onRefresh" :title="trans('refresh')" :disabled="busy">
          <i class="fa fa-fw fa-redo" v-if="!busy"></i>
          <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-table
          id="images"
          ref="table"
          class="bg-white table-row-click"
          bordered
          small
          responsive
          show-empty
          :busy="busy"
          :fields="fields"
          :items="provider"
          :per-page="perPage"
          :current-page="currentPage"
          @row-clicked="onRowClick"
        >
          <template v-slot:empty>
            <p align="center">{{ trans('no_image_found') }}</p>
          </template>
          <template v-slot:table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>{{ trans('please_wait') }}</strong>
            </div>
          </template>
          <template v-slot:cell(name)="data">
            {{ data.value }}
            <sup v-if="data.item.is_from_canva">
              <b-badge variant="canva">
                Canva
              </b-badge>
            </sup>
            <sup v-if="data.item.publish_to_partner">
              <b-badge variant="primary">
                {{ organization.can_publish && organization.partner == data.item.publish_to_partner ? trans('published') : data.item.publish_to_partner }}
              </b-badge>
            </sup>
            <tags
              v-if="data.item.organization_id == organization.id"
              :organization="organization"
              :model="data.item"
              :hide-edit="imageNameOnly"
              type="image"
              :all-tags="all_tags"
              @all-tags="all_tags = $event"
              :disabled="busy"
            ></tags>
          </template>
          <template v-slot:cell(w170_url)="data">
            <div class="img-wrapper text-center">
              <img :src="data.value" alt="data.item.name" />
            </div>
          </template>
          <template v-slot:cell(action)="data">
            <b-button
              v-if="imageNameOnly && !data.item.deleted_at"
              variant="link"
              size="sm"
              class="p-0"
              :title="`${trans('duplicate')}
              ${trans('crop_resize').toLowerCase()}`"
              @click="onClickCrop(data.item)"
            >
              <i class="fa fa-fw fa-copy"></i>
            </b-button>
            <b-dropdown v-if="!imageNameOnly" variant="link" size="sm" class="p-0" toggle-class="text-muted" boundary="window" right>
              <template #button-content>
                <i class="fa fa-fw fa-ellipsis-h"></i>
              </template>
              <b-dropdown-item :href="data.item.url" target="_blank">
                <i class="fa fa-fw fa-external-link-alt"></i>
                {{ trans('download') }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!data.item.deleted_at" @click="onClickDuplicate(data.item)">
                <i class="fa fa-fw fa-copy"></i>
                {{ trans('duplicate') }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!data.item.deleted_at" @click="onClickCrop(data.item)">
                <i class="fa fa-fw fa-copy"></i>
                {{ `${trans('duplicate')} & ${trans('crop_resize').toLowerCase()}` }}
              </b-dropdown-item>
              <b-dropdown-divider v-if="!data.item.deleted_at"></b-dropdown-divider>
              <b-dropdown-item v-if="!data.item.deleted_at" @click="onClickArchive(data.item)" link-class="text-danger">
                <i class="fa fa-fw fa-trash"></i>
                {{ trans('archive') }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!!data.item.deleted_at" @click="onClickRestore(data.item)" link-class="text-success">
                <i class="fa fa-fw fa-trash-restore"></i>
                {{ trans('restore') }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!!data.item.deleted_at" @click="onClickForceDelete(data.item)" link-class="text-danger">
                <i class="fa fa-fw fa-trash"></i>
                {{ trans('delete') }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <b-pagination
          :per-page="perPage"
          :current-page="currentPage"
          :total-rows="totalRows"
          v-model="currentPage"
          aria-controls="images"
          align="center"
        />
      </b-col>
    </b-row>

    <image-modal
      v-if="!noOpen"
      :organization="organization"
      :image="item"
      @saved="onRefresh"
      @deleted="onRefresh"
      @canva="onClickModifyInCanva"
      @crop="onClickCrop"
    ></image-modal>
    <image-crop-modal
      id="table-image-crop-modal"
      :organization="organization"
      :image="item"
      @created="onCreated"
    ></image-crop-modal>
    <duplicate-media-modal
      :organization="organization"
      :media="item"
      @duplicated="onDuplicated"
    ></duplicate-media-modal>

    <b-modal
      id="upload-image-modal"
      :title="trans('upload')"
      hide-footer
      v-if="!hideUpload"
    >
      <div class="text-center">
        <b-form-group>
          <b-form-file
            v-model="upload_file"
            :placeholder="trans('upload_image')"
            :drop-placeholder="trans('drop_file_here')"
            accept="image/jpeg, image/png, image/gif"
            :disabled="busy"
            @input="onImageFileChange(null)"
          />
        </b-form-group>
        <p class="lead" v-if="busy">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('uploading') }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<style>
  table#images td>.img-wrapper>img {
    max-height: 170px;
  }
</style>

<script>
  import ImageModal from './ImageModal'
  import Tags from './../tag/Tags'
  import TagsFilter from './../tag/TagsFilter'
  import ImageCropModal from './ImageCropModal'
  import DuplicateMediaModal from './DuplicateMediaModal'

  export default {
    props: {
      organization: Object,
      hideUpload: {
        type: Boolean,
        default: false,
      },
      hideCanva: {
        type: Boolean,
        default: false,
      },
      imageNameOnly: Boolean, // image-name-only
      noOpen: Boolean, // no-open
      search: String,
      perPage: {
        type: Number,
        default: 5
      },
    },
    components: {
      ImageModal,
      Tags,
      TagsFilter,
      ImageCropModal,
      DuplicateMediaModal,
    },
    data() {
      let fields = [
        {key: 'w170_url', label: trans('image')},
        {key: 'name', label: trans('name')},
      ]
      if (!this.imageNameOnly) {
        fields.push({
          key: 'description',
          label: trans('description'),
          class: 'table-row-width-200',
        })
        fields.push({key: 'extension', label: trans('type')})
        fields.push({key: 'human_readable_size', label: trans('size')})
        fields.push({
          key: 'created_at',
          label: trans('date'),
          formatter(value) {
            return moment(value).format('LLL')
          },
        })
      }
      fields.push({key: 'action', label: ''})

      return {
        trans: trans,
        moment: moment,
        error: null,
        busy: false,
        fields: fields,
        items: [],
        currentPage: 1,
        totalRows: 0,
        item: null,
        upload_file: null,
        upload_url: null,
        query: this.search || null,
        tags: null,
        all_tags: null,
        tag_is_none: false,
        canvaApi: null,
        filterType: null,
      }
    },
    methods: {
      onClickCrop(item) {
        this.item = item
        this.$bvModal.show('table-image-crop-modal')
      },
      onClickDuplicate(item) {
        this.item = item
        this.$bvModal.show('duplicate-media-modal')
      },
      onClickColumnFilter(type) {
        this.filterType = type
        this.onSearch()
      },
      onClickArchive(item) {
        this.$bvModal.msgBoxConfirm(
          trans('archive_confirm'),
          {
            title: item.name,
            okTitle: trans('archive'),
            okVariant: 'danger',
            cancelTitle: trans('cancel'),
          }
        )
        .then(confirmed => {
          if (!confirmed) return
          let url = `/organization/${this.organization.id}/media/image/${item.id}`
          axios.delete(url)
          .then(response => {
            _.toastSuccess(this, response.data.message)
            this.onRefresh()
          })
          .catch(error => {
            this.error = _.errorFromAjax(error)
            _.toastError(this, this.error)
          })
        })
      },
      onClickForceDelete(item) {
        this.$bvModal.msgBoxConfirm(
          trans('media_delete_confirm'),
          {
            title: item.name,
            okTitle: trans('delete'),
            okVariant: 'danger',
            cancelTitle: trans('cancel'),
          }
        )
        .then(confirmed => {
          if (!confirmed) return
          let url = `/organization/${this.organization.id}/media/image/${item.id}`
          axios.delete(url)
          .then(response => {
            _.toastSuccess(this, response.data.message)
            this.onRefresh()
          })
          .catch(error => {
            this.error = _.errorFromAjax(error)
            _.toastError(this, this.error)
          })
        })
      },
      onClickRestore(item) {
        const url = `/organization/${this.organization.id}/media/image/${item.id}/restore`
        axios.get(url)
        .then(response => {
          this.$bvToast.toast(response.data.message, {
            title: trans('success'),
            variant: 'success',
            solid: true,
          })
          this.onRefresh()
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onClickCanva(width, height) {
        this.canvaApi.createDesign({
          design: {
            type: 'EmailHeader',
            dimensions: {
              units: 'px',
              width: width,
              height: height,
            },
          },
          editor: {
            publishLabel: trans('save'),
            fileType: 'png',
          },
          onDesignOpen: ({designId}) => {
            // Triggered when editor finishes loading and opens a new design.
            // You can save designId for future use.
            console.log('onDesignOpen', designId)
          },
          onDesignPublish: ({exportUrl, designId}) => {
            // Triggered when design is published to an image.
            // Save the image to your server as the exportUrl will expire shortly.
            console.log('onDesignPublish', exportUrl, designId)
            this.onPublishNewCanvaImage(exportUrl, designId)
          },
          onDesignClose: () => {
            // Triggered when editor is closed.
            console.log('onDesignClose')
          },
        })
      },
      onClickModifyInCanva() {
        this.modifyInCanva(this.item.canva_id)
      },
      modifyInCanva(canva_id) {
        this.canvaApi.editDesign({
          design: {
            id: canva_id,
          },
          editor: {
            publishLabel: trans('save'),
            fileType: 'png',
          },
          onDesignOpen: ({designId}) => {
            // Triggered when editor finishes loading and opens a new design.
            // You can save designId for future use.
            console.log('onDesignOpen', designId)
          },
          onDesignPublish: ({exportUrl, designId}) => {
            // Triggered when design is published to an image.
            // Save the image to your server as the exportUrl will expire shortly.
            console.log('onDesignPublish', exportUrl, designId)
            this.onPublishNewCanvaImage(exportUrl, designId)
          },
          onDesignClose: () => {
            // Triggered when editor is closed.
            console.log('onDesignClose')
          },
        })
      },
      onPublishNewCanvaImage(canva_url, canva_id) {
        this.busy = true
        this.error = null
        const url = `/organization/${this.organization.id}/media/image/canva`
        axios.post(url, {
          canva_url: canva_url,
          canva_id: canva_id,
        })
        .then(response => {
          this.busy = false
          this.upload_url = response.data.url
          if (this.noOpen) {
            this.$emit('choose', response.data)
            return
          }
          this.currentPage = 1
          this.$refs.table.refresh()
          this.$bvModal.hide('upload-image-modal')
        })
        .catch(this.handleError)
      },
      onDuplicated() {
        this.currentPage = 1
        this.$refs.table.refresh()
      },
      onSearch() {
        this.currentPage = 1
        this.$refs.table.refresh()
        this.$emit('search', this.query)
      },
      onCreated(image) {
        if (this.noOpen) {
          this.$emit('choose', image)
          return
        }
        this.upload_url = image.url
        this.currentPage = 1
        this.onRefresh()
      },
      onImageFileChange() {
        if (!this.upload_file) return
        var reader = new FileReader();
        reader.readAsDataURL(this.upload_file);
        reader.onload = (e) => {
          this.item = {
            file_name: _.removeDiacritics(this.upload_file.name),
            data_url: e.target.result,
            size: this.upload_file.size,
            mime_type: this.upload_file.type,
            file: this.upload_file
          }
          this.busy = false
          this.$bvModal.hide('upload-image-modal')
          this.$bvModal.show('table-image-crop-modal')
        }
      },
      onRefresh() {
        this.$refs.table.refresh()
      },
      onRowClick(item) {
        this.$emit('choose', item)
        if (this.noOpen) return
        this.item = item
        setTimeout(() => {
          this.$bvModal.show('show-image-modal')
        }, 1)
      },
      provider() {
        this.error = null
        this.busy = true
        let url = '/organization/' + this.organization.id + '/media/list' +
          '?page=' + this.currentPage + '&per_page=' + this.perPage
        if (this.query) url += '&name=' + encodeURIComponent(this.query)
        if (this.tag_is_none) url += `&tag=__none__`
        else if (this.tags && this.tags.length > 0) {
          this.tags.forEach(tag => {
            url += `&tags[]=${encodeURIComponent(tag)}`
          })
        }
        if (this.filterType) url += `&type=${this.filterType}`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.currentPage = response.data.current_page
          this.totalRows = response.data.total
          this.items = response.data.data
          return this.items
        })
        .catch(error => {
          this.busy = false
          this.handleError(error)
          this.items = []
          return this.items
        })
      },
      handleError(error) {
        this.busy = false
        this.error = _.errorFromAjax(error)
        this.$bvToast.toast(this.error, {
          title: trans('error'),
          variant: 'danger',
          solid: true,
        })
      },
    },
    mounted() {
      window.imagesTableComponent = this
      _.loadCanva(canvaApi => {
        this.canvaApi = canvaApi
      })
    },
  }
</script>